import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const TermsConditions = () => {
  return (
    <div className="bg-black text-white min-h-screen py-20">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white">Terms & Conditions</CardTitle>
            <p className="text-zinc-400 text-sm mt-2">Last Updated: December 17, 2025</p>
          </CardHeader>
          <CardContent className="space-y-6 text-zinc-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Acceptance of Terms</h2>
              <p>
                By accessing and using the Sauce Sity Barbershop website and services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Services</h2>
              <p className="mb-2">Sauce Sity Barbershop provides:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Professional barbering and grooming services</li>
                <li>Online appointment booking system</li>
                <li>Membership and loyalty rewards programs</li>
                <li>SMS appointment notifications and reminders</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">SMS Text Messaging Terms</h2>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-white mb-2">Message Frequency</h3>
                  <p>
                    By providing your mobile phone number and opting in, you consent to receive automated text messages from Sauce Sity Barbershop. Message frequency varies and may include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
                    <li>Appointment confirmations (sent immediately upon booking)</li>
                    <li>Appointment reminders (typically 24 hours before appointment)</li>
                    <li>Occasional service updates and promotional offers (max 2-4 per month)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">Message & Data Rates</h3>
                  <p className="text-sm">
                    <strong>Message and data rates may apply.</strong> Standard carrier messaging rates apply based on your mobile plan. Sauce Sity Barbershop does not charge for messages, but your carrier may.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">How to Opt Out</h3>
                  <p className="text-sm mb-2">
                    <strong>Reply STOP to opt out.</strong> You can cancel SMS notifications at any time by replying STOP to any text message from us. You will receive a confirmation message that you have been unsubscribed. After this, you will no longer receive SMS messages unless you re-opt-in.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-white mb-2">How to Get Help</h3>
                  <p className="text-sm">
                    <strong>Reply HELP for help.</strong> For assistance, text HELP to any message or contact us at (239) 201-8105. You can also email us at info@saucesity.com.
                  </p>
                </div>

                <div className="bg-zinc-800 rounded p-3 mt-3">
                  <p className="text-xs text-zinc-400">
                    Carriers are not liable for delayed or undelivered messages. We respect your privacy and will never share your mobile information with third parties for marketing purposes.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Appointment Booking and Cancellations</h2>
              <div className="space-y-2">
                <p><strong>Booking:</strong> All appointments must be booked through our online system or by calling (239) 201-8105.</p>
                <p><strong>Cancellation Policy:</strong> Please provide at least 24 hours notice for cancellations. Late cancellations or no-shows may result in a cancellation fee or deposit requirement for future bookings.</p>
                <p><strong>No-Show Policy:</strong> Customers with multiple no-shows may be required to provide a deposit or pre-payment for future appointments.</p>
                <p><strong>Arrival:</strong> Please arrive 5-10 minutes before your scheduled appointment time.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Payment Terms</h2>
              <div className="space-y-2">
                <p><strong>Payment Methods:</strong> We accept credit cards, debit cards, and cash. All online payments are processed securely through Stripe.</p>
                <p><strong>Pricing:</strong> Service prices are subject to change. Current prices are available on our website and in-store.</p>
                <p><strong>Membership Fees:</strong> Membership fees are charged monthly and are non-refundable once processed.</p>
                <p><strong>Refunds:</strong> Service fees are generally non-refundable. Refunds may be issued at our discretion for exceptional circumstances.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Loyalty Rewards Program</h2>
              <div className="space-y-2">
                <p><strong>Earning Points:</strong> Customers earn 4 loyalty points for every $1 spent on services.</p>
                <p><strong>Redemption:</strong> Points can be redeemed for rewards as specified in the rewards catalog.</p>
                <p><strong>Expiration:</strong> Points do not expire as long as your account remains active.</p>
                <p><strong>Account Activity:</strong> Accounts inactive for 12+ months may have points reset.</p>
                <p><strong>Termination:</strong> We reserve the right to terminate loyalty accounts for fraudulent activity or abuse of the program.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Membership Terms</h2>
              <div className="space-y-2">
                <p><strong>Benefits:</strong> Membership benefits include priority booking, exclusive hours, and special discounts as advertised.</p>
                <p><strong>Billing:</strong> Memberships are billed monthly on the date of sign-up.</p>
                <p><strong>Cancellation:</strong> Memberships can be cancelled at any time. Cancellations take effect at the end of the current billing period.</p>
                <p><strong>Changes:</strong> We reserve the right to modify membership benefits and pricing with 30 days notice.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">User Conduct</h2>
              <p className="mb-2">You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Provide false or misleading information</li>
                <li>Abuse or harass our staff or other customers</li>
                <li>Use our services for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
              <p className="mt-3">
                We reserve the right to refuse service or terminate accounts for violations of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Limitation of Liability</h2>
              <p>
                Sauce Sity Barbershop and its employees shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services. Our liability is limited to the amount paid for the service in question.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, and images, is the property of Sauce Sity Barbershop and is protected by copyright and trademark laws. Unauthorized use is prohibited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Privacy</h2>
              <p>
                Your use of our services is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices regarding your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to the website. Continued use of our services after changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Governing Law</h2>
              <p>
                These Terms and Conditions are governed by the laws of the State of Florida. Any disputes arising from these terms shall be resolved in the courts of Lee County, Florida.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-3">Contact Information</h2>
              <div className="bg-zinc-800 rounded-lg p-4 mt-3">
                <p className="font-semibold text-white">Sauce Sity Barbershop</p>
                <p>Phone: (239) 201-8105</p>
                <p>Email: info@saucesity.com</p>
                <p>Address: Cape Coral, FL</p>
              </div>
            </section>

            <section className="border-t border-zinc-700 pt-6">
              <p className="text-sm text-zinc-400">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsConditions;
